/**
 * @file template-restore.ts
 * @description «Вернуть элемент» из окна публикации (phase19.16 Р2а;
 * uiux-design-r2a §6 «Публикация без обязательного: Не опубликовано: в
 * шаблоне нет «Сведения о товаре». [Вернуть элемент]»; SB34).
 *
 * Чинит одно замечание шаблона в черновике, не трогая остальное:
 *  - элемента нет — ставит его с умолчаниями в блок с «Ценой и покупкой»
 *    (иначе в первый блок), в колонку покупки;
 *  - элемент скрыт — снимает «скрыть» у элемента, показывает блок и
 *    включает блок на всех устройствах.
 * Шаблона без блоков нет смысла чинить по одному — тогда «Сбросить к
 * стандартному». Запись — обычной правкой блока (сервер проверяет шаблон).
 */

import { isProductRequiredKind, type ProductRequiredKind, type SectionPayload, type TemplateIssue } from "@chaiberi/shared";

import { sitePages, type SitePageBlock } from "../api-site-pages";
import { siteTemplates } from "../api-site-templates";
import { blockTitle } from "../../components/site-pages/block-meta";
import { blockErrorText } from "./block-errors";
import { insertElement, replaceElement } from "./section-edit";
import { targetColumn } from "./template-rules";

/** Что сделано: вставлен, показан или шаблон сброшен целиком. */
export type RestoreOutcome = "inserted" | "shown" | "reset";

/** Причина, понятная владельцу (колонка заполнена и т. п.). */
export class RestoreError extends Error {}

/** Текст ошибки «Вернуть элемент»: своя причина, ответ сервера или запасная фраза. */
export function restoreErrorText(err: unknown): string {
  if (err instanceof RestoreError) return err.message;
  return blockErrorText(err, "Не удалось вернуть элемент — откройте шаблон и добавьте его вручную.");
}

function findKind(blocks: readonly SitePageBlock[], kind: string): { block: SitePageBlock; elementId: string } | null {
  for (const block of blocks) {
    const payload = block.payload as SectionPayload;
    for (const column of payload.columns?.items ?? []) {
      const el = column.elements.find((e) => e.kind === kind);
      if (el) return { block, elementId: el.id };
    }
  }
  return null;
}

/** Вернуть обязательный элемент шаблона в черновике. */
export async function restoreRequiredElement(kind: ProductRequiredKind): Promise<RestoreOutcome> {
  const { page } = await siteTemplates.product();
  const blocks = page.blocks.filter((b) => b.type === "SECTION");
  if (blocks.length === 0) {
    await siteTemplates.reset();
    return "reset";
  }
  const found = findKind(blocks, kind);
  if (found) {
    const payload = found.block.payload as SectionPayload;
    const at = payload.columns.items.flatMap((c) => c.elements).find((e) => e.id === found.elementId);
    const shown = at ? replaceElement(payload, found.elementId, { ...at, hide: undefined }) : payload;
    const next: SectionPayload = { ...shown, look: { ...shown.look, visibility: { phone: true, tablet: true, desktop: true } } };
    await sitePages.updateBlock(found.block.id, { payload: next, isEnabled: true, expectedUpdatedAt: found.block.updatedAt });
    return "shown";
  }
  // Блок-получатель — как при переносе обязательных: с «Ценой и покупкой», иначе первый.
  const target = findKind(blocks, "product-buy")?.block ?? blocks[0]!;
  const payload = target.payload as SectionPayload;
  const inserted = insertElement(payload, targetColumn(payload), kind);
  if (!inserted) throw new RestoreError(`Колонка блока «${blockTitle(target)}» заполнена — уберите один элемент и нажмите «Вернуть элемент» ещё раз.`);
  await sitePages.updateBlock(target.id, { payload: inserted.payload, expectedUpdatedAt: target.updatedAt });
  return "inserted";
}

/**
 * Какой обязательный элемент можно вернуть по замечанию шаблона: нет его
 * или он скрыт; иначе `null` (повтор, лимит, второй H1 — чинится вручную).
 */
export function restorableKind(issue: Pick<TemplateIssue, "code" | "kind">): ProductRequiredKind | null {
  if (issue.code !== "required_missing" && issue.code !== "required_hidden") return null;
  return issue.kind && isProductRequiredKind(issue.kind) ? issue.kind : null;
}
